import {
  Button,
  Checkbox,
  ClickAwayListener,
  DialogActions,
  FormControlLabel,
  Grid,
  Box
} from '@mui/material'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import { CloseIcon } from 'assets/icons'
import { FormEvent, ReactNode, useRef, useState } from 'react'
import Logger from 'services/Logger'
import { theme } from 'theme'
import { t } from 'i18next'
import { StyledDialog } from './styled.dialog'
import i18n from 'i18n'
import { CheckBox } from '@mui/icons-material'
import LocalStorage from 'services/LocalStorage'
import { isMobileDevice } from 'utils/constants'

type ConfirmationDialogProps = {
  rightButtonColor?: string
  leftButtonColor?: string
  leftButton?: string
  rightButton?: string
  leftButtonDisabled?: boolean
  showCloseIcon?: boolean
  rightButtonHidden?: boolean
  leftButtonHidden?: boolean
  title: string
  content: ReactNode | string
  handleLeftButton?: () => any
  open: boolean
  handleClose?: () => any
  handleRightButton?: () => any
  pointerEvents?: 'all' | 'none' | undefined
  followForm?: boolean
  contentDir?: 'ltr' | 'rtl'
  noButtons?: boolean
  doNotShowButtonEnabled?: boolean
  doNotShowButtonKey?: string
  doNotShowButtonValue?: string
  rightButtonDisabled?: boolean
  titlePadding?: string
  fullWidth?: boolean
  classes?: any
  scroll?: any
  disableScrollLock?: boolean
  rightButtonId?: string
  leftButtonId?: string
  noPaddingBottom?: boolean
}

export default function ConfirmationDialog(props: ConfirmationDialogProps) {
  const rightButtonRef = useRef<HTMLButtonElement>(null)

  const handleSubmit = (e: FormEvent) => {
    if (rightButtonRef.current?.disabled) {
      e.preventDefault()
      return
    }
    e.preventDefault()
    if (handleRightButton) handleRightButton()
  }
  const {
    open,
    title,
    content,
    rightButtonHidden,
    leftButtonDisabled,
    rightButtonColor,
    rightButton,
    showCloseIcon,
    followForm,
    leftButtonColor,
    leftButtonHidden,
    handleLeftButton,
    leftButton,
    handleRightButton,
    handleClose: handleCancel,
    rightButtonDisabled: rightButtonDisabled,
    ...rest
  } = props

  const [dontShowAgain, onDontShowAgain] = useState(false)
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDontShowAgain(event.target.checked)
  }

  const handleLeft = () => {
    if (handleLeftButton) handleLeftButton()

    if (
      dontShowAgain &&
      props.doNotShowButtonKey &&
      props.doNotShowButtonEnabled &&
      props.doNotShowButtonValue
    ) {
      LocalStorage.set(props.doNotShowButtonKey, props.doNotShowButtonValue)
    }
  }

  const handleRight = () => {
    if (handleRightButton) handleRightButton()

    if (
      dontShowAgain &&
      props.doNotShowButtonKey &&
      props.doNotShowButtonEnabled &&
      props.doNotShowButtonValue
    ) {
      LocalStorage.set(props.doNotShowButtonKey, props.doNotShowButtonValue)
    }
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleCancel}
      disableScrollLock={isMobileDevice()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        style: { borderRadius: 8 }
      }}
      {...rest}
    >
      <Box style={{ position: 'relative', height: '100%' }}>
        {showCloseIcon && (
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            id={'closeDialogButton'}
            sx={{
              position: 'absolute',
              right: i18n.dir() === 'ltr' ? 14 : undefined,
              left: i18n.dir() === 'rtl' ? 14 : undefined,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <form onSubmit={handleSubmit}>
          <DialogTitle
            id="alert-dialog-title"
            style={{
              textAlign: 'center',
              fontSize: '1.5rem',
              padding: props.titlePadding
            }}
          >
            <div style={{ marginTop: showCloseIcon ? 16 : undefined }}>
              {title}
            </div>
          </DialogTitle>
          <Box className="dialog-content-box" style={{ overflowX: 'auto' }}>
            {content !== undefined && content !== null && (
              <Grid
                container
                display={'flex'}
                alignItems={'center'}
                sx={{
                  paddingLeft: isMobileDevice() ? '1rem' : 8,
                  paddingRight: isMobileDevice() ? '1rem' : 8,
                  paddingBottom: props.noPaddingBottom ? 0 : 8,
                  display: 'flex',
                  width: '100%'
                }}
              >
                {typeof content === 'string' ? (
                  <pre
                    style={{
                      textAlign: 'center',
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {content}
                  </pre>
                ) : (
                  content
                )}
                {props.doNotShowButtonEnabled && (
                  <FormControlLabel
                    sx={{
                      marginTop: '32px'
                    }}
                    control={
                      <Checkbox
                        checked={dontShowAgain}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={t('dontShowAgain')}
                  />
                )}
              </Grid>
            )}
          </Box>
          {props.noButtons ? (
            <></>
          ) : (
            <DialogActions>
              {leftButtonHidden ? (
                <></>
              ) : (
                <Button
                  id={props.leftButtonId ?? 'declineButton'}
                  focusRipple={false}
                  disabled={leftButtonDisabled ?? false}
                  onClick={handleLeft}
                  style={{
                    color: leftButtonColor
                      ? leftButtonColor
                      : theme.palette.button.text.groupcal,
                    opacity: props.leftButtonDisabled ? 0.5 : 1
                  }}
                >
                  {leftButton ?? i18n.t('cancel')}
                </Button>
              )}

              {rightButtonHidden ? (
                <></>
              ) : (
                <Button
                  ref={rightButtonRef}
                  id={props.rightButtonId ?? 'confirmButton'}
                  autoFocus
                  type={!followForm ? 'button' : 'submit'}
                  focusRipple={false}
                  style={{
                    color: rightButtonColor
                      ? rightButtonColor
                      : theme.palette.button.text.groupcal,
                    opacity: props.rightButtonDisabled ? 0.5 : 1
                  }}
                  disabled={rightButtonDisabled ?? false}
                  onClick={handleRightButton ? handleRight : handleCancel}
                >
                  {rightButton ?? i18n.t('ok')}
                </Button>
              )}
            </DialogActions>
          )}
        </form>
      </Box>
    </StyledDialog>
  )
}
